/**
 * One async read per key (the /looker run-key pattern, once): it runs when the
 * key changes, never shows a result read under another key, and turns a
 * rejection into 'failed' (logged), never into an empty answer. '' = idle.
 * `run` is read from a ref, so a new closure each render does not restart the
 * read; only the key does.
 *
 * @module hooks/useKeyedRead
 */
import { useEffect, useRef, useState } from 'react';
import { logError } from '@/services/logger';
import type { ReadState } from '@/services/overview/overviewTypes';

const IDLE = { status: 'idle' } as const;
const LOADING = { status: 'loading' } as const;

export const useKeyedRead = <T>(key: string, run: () => Promise<T>, label: string): ReadState<T> => {
  const [result, setResult] = useState<{ key: string; state: ReadState<T> } | null>(null);
  const latest = useRef(run);
  latest.current = run;

  useEffect(() => {
    if (!key) return undefined;
    let ignore = false;
    latest.current().then(
      (data) => { if (!ignore) setResult({ key, state: { status: 'ready', data } }); },
      (err: unknown) => {
        logError(label, 'read failed', err);
        if (!ignore) setResult({ key, state: { status: 'failed' } });
      },
    );
    return () => { ignore = true; };
  }, [key, label]);

  if (!key) return IDLE;
  return result && result.key === key ? result.state : LOADING;
};
